import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setActiveOrganisationId } from "../lib/api";

const AuthCtx = createContext(null);

const FONT_FAMILIES = {
  system: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  plex: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  arial: 'Arial, Helvetica, sans-serif',
  verdana: 'Verdana, Geneva, sans-serif',
  atkinson: '"Atkinson Hyperlegible", Arial, sans-serif',
  tahoma: 'Tahoma, Geneva, sans-serif',
  trebuchet: '"Trebuchet MS", Arial, sans-serif',
  georgia: 'Georgia, "Times New Roman", serif',
};

const FONT_SIZES = { small: "15px", base: "16px", comfortable: "17px", large: "18px", xlarge: "20px", xxlarge: "22px" };
const BUTTON_SIZES = {
  compact: { minHeight: "36px", paddingY: "0.4rem", paddingX: "0.75rem", fontSize: "0.75rem" },
  base: { minHeight: "40px", paddingY: "0.5rem", paddingX: "1rem", fontSize: "0.875rem" },
  large: { minHeight: "46px", paddingY: "0.65rem", paddingX: "1.125rem", fontSize: "1rem" },
  xlarge: { minHeight: "52px", paddingY: "0.8rem", paddingX: "1.25rem", fontSize: "1.125rem" },
};

function getDevicePreference(prefs, device, key, legacyKey, fallback) {
  return prefs[`${device}_${key}`] || prefs[legacyKey] || fallback;
}

function setButtonVariables(prefix, buttonSize) {
  document.documentElement.style.setProperty(`--user-${prefix}-control-min-height`, buttonSize.minHeight);
  document.documentElement.style.setProperty(`--user-${prefix}-control-padding-y`, buttonSize.paddingY);
  document.documentElement.style.setProperty(`--user-${prefix}-control-padding-x`, buttonSize.paddingX);
  document.documentElement.style.setProperty(`--user-${prefix}-control-font-size`, buttonSize.fontSize);
}

function applyUserPreferences(preferences) {
  if (typeof document === "undefined") return;
  const prefs = preferences || {};
  const family = FONT_FAMILIES[prefs.font_family] || FONT_FAMILIES.system;
  const mobileFontSize = FONT_SIZES[getDevicePreference(prefs, "mobile", "font_size", "font_size", "base")] || FONT_SIZES.base;
  const desktopFontSize = FONT_SIZES[getDevicePreference(prefs, "desktop", "font_size", "font_size", "base")] || FONT_SIZES.base;
  const mobileButtonSize = BUTTON_SIZES[getDevicePreference(prefs, "mobile", "button_size", "button_size", "base")] || BUTTON_SIZES.base;
  const desktopButtonSize = BUTTON_SIZES[getDevicePreference(prefs, "desktop", "button_size", "button_size", "base")] || BUTTON_SIZES.base;
  document.documentElement.style.fontSize = "";
  document.documentElement.style.setProperty("--user-mobile-root-font-size", mobileFontSize);
  document.documentElement.style.setProperty("--user-desktop-root-font-size", desktopFontSize);
  document.documentElement.style.setProperty("--user-font-family", family);
  document.documentElement.style.setProperty("--user-heading-font-family", family);
  document.documentElement.style.setProperty("--user-font-size", "0.875rem");
  setButtonVariables("mobile", mobileButtonSize);
  setButtonVariables("desktop", desktopButtonSize);
}


export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown, false = anon
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    applyUserPreferences(user && user.user_preferences);
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) {
          setActiveOrganisationId(data.organisation_id);
          setUser(data);
        }
      } catch (err) {
        if (err?.response?.status && err.response.status !== 401) {
          // 401 is the normal "not signed in" case; anything else is worth logging
          console.error("AuthContext /auth/me failed:", err);
        }
        if (!cancelled) setUser(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password, rememberMe = false) => {
    const { data } = await api.post("/auth/login", { email, password, remember_me: rememberMe });
    setActiveOrganisationId(data.user.organisation_id);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("AuthContext logout failed:", err);
    }
    setUser(false);
    setActiveOrganisationId(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshMe: async () => {
      const { data } = await api.get("/auth/me");
      setActiveOrganisationId(data.organisation_id);
      setUser(data);
      return data;
    } }),
    [user, loading, login, logout]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
